import React, { useEffect, useMemo, useState } from 'react'
import { adminService, type CreateStaffPayload, type Permission } from '@/services/adminService'
import { useAuth } from '@/hooks/useAuth'

export interface CreateStaffModalProps {
  isOpen: boolean
  onClose: () => void
  role: 'admin' | 'super_admin'
  onSuccess?: () => void
  // Injected in tests; production uses module-level adminService.
  service?: typeof adminService
}

/**
 * CreateStaffModal lets a super admin mint a new admin or super_admin.
 *
 * When role=admin, the form also renders a multi-select of every permission
 * code grouped by module so the new admin is useful from minute one. When
 * role=super_admin, permissions are meaningless (super_admin bypasses every
 * check) and the user is forced to tick an acknowledgement checkbox before
 * the submit button becomes enabled.
 */
export const CreateStaffModal: React.FC<CreateStaffModalProps> = ({
  isOpen,
  onClose,
  role,
  onSuccess,
  service = adminService,
}) => {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [granted, setGranted] = useState<Set<string>>(new Set())
  const [catalog, setCatalog] = useState<Record<string, Permission[]>>({})
  const [acknowledged, setAcknowledged] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadingCatalog, setLoadingCatalog] = useState(false)

  // Reset form state whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setName('')
    setEmail('')
    setPassword('')
    setPhone('')
    setGranted(new Set())
    setAcknowledged(false)
    setError(null)
  }, [isOpen, role])

  // Lazy-load permission catalog only when the admin form is open.
  useEffect(() => {
    if (!isOpen || role !== 'admin') return
    let mounted = true
    setLoadingCatalog(true)
    service
      .listPermissions()
      .then((res) => {
        if (!mounted) return
        if (res.success && res.data) setCatalog(res.data.modules || {})
      })
      .catch(() => undefined)
      .finally(() => mounted && setLoadingCatalog(false))
    return () => {
      mounted = false
    }
  }, [isOpen, role, service])

  const moduleKeys = useMemo(() => Object.keys(catalog).sort(), [catalog])

  const filteredCatalog = useMemo(() => {
    if (isSuperAdmin) return catalog
    const userPerms = user?.permissions ?? []
    const filtered: Record<string, Permission[]> = {}
    for (const mod of moduleKeys) {
      const perms = catalog[mod].filter((p) => userPerms.includes(p.code))
      if (perms.length > 0) filtered[mod] = perms
    }
    return filtered
  }, [catalog, moduleKeys, isSuperAdmin, user])

  const filteredModuleKeys = useMemo(() => Object.keys(filteredCatalog).sort(), [filteredCatalog])

  const togglePermission = (code: string) => {
    setGranted((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const validate = (): string | null => {
    if (!name.trim()) return 'Nama wajib diisi'
    if (!email.trim() || !email.includes('@')) return 'Email tidak valid'
    if (password.length < 8) return 'Password minimal 8 karakter'
    if (role === 'admin' && granted.size === 0) return 'Pilih minimal 1 hak akses'
    if (role === 'super_admin' && !acknowledged) {
      return 'Harap centang konfirmasi pemahaman'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const msg = validate()
    if (msg) {
      setError(msg)
      return
    }
    setError(null)
    setSaving(true)
    try {
      const payload: CreateStaffPayload = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        role,
        phone: phone.trim() || undefined,
        permissionCodes: role === 'admin' ? Array.from(granted) : undefined,
      }
      const res = await service.createStaff(payload)
      if (!res.success) throw new Error(res.error?.message || 'Gagal menyimpan')
      onSuccess?.()
      onClose()
    } catch (err) {
      const e = err as Error
      setError(e.message || 'Gagal menyimpan')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const title = role === 'super_admin' ? 'Tambah Super Admin' : 'Tambah Admin'
  const canSubmit = !saving && (role === 'admin' || acknowledged)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="create-staff-modal"
    >
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            aria-label="tutup"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="staff-name">
              Nama lengkap
            </label>
            <input
              id="staff-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all"
              data-testid="staff-name-input"
              autoComplete="new-password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="staff-email">
              Email
            </label>
            <input
              id="staff-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all"
              data-testid="staff-email-input"
              autoComplete="new-password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="staff-password">
              Password (minimal 8 karakter)
            </label>
            <input
              id="staff-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all"
              data-testid="staff-password-input"
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1" htmlFor="staff-phone">
              Telepon (opsional)
            </label>
            <input
              id="staff-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:border-primary/50 transition-all"
              data-testid="staff-phone-input"
            />
          </div>

          {role === 'admin' && (
            <div data-testid="staff-permissions-section">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Hak akses</h3>
              {loadingCatalog && <p className="text-sm text-gray-500 dark:text-gray-400">Memuat izin...</p>}
              {!loadingCatalog && filteredModuleKeys.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Tidak ada izin yang tersedia.</p>
              )}
              <div className="space-y-3">
                {filteredModuleKeys.map((mod) => (
                  <fieldset
                    key={mod}
                    className="border border-slate-200 dark:border-white/10 rounded-lg p-4"
                  >
                    <legend className="px-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {mod}
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                      {filteredCatalog[mod].map((p) => (
                        <label key={p.code} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer hover:text-primary transition-colors">
                          <input
                            type="checkbox"
                            checked={granted.has(p.code)}
                            onChange={() => togglePermission(p.code)}
                            className="rounded-md border-slate-300 text-primary focus:ring-primary"
                            data-testid={`staff-perm-${p.code}`}
                          />
                          <span title={p.description} className="font-medium">{p.code}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            </div>
          )}

          {role === 'super_admin' && (
            <label className="flex items-start gap-2 p-3 rounded bg-red-500/10 border border-red-500/30">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
                data-testid="staff-acknowledge"
              />
              <span className="text-xs text-red-200">
                Saya paham super admin memiliki akses penuh tanpa pembatasan.
              </span>
            </label>
          )}

          {error && (
            <div
              role="alert"
              data-testid="staff-form-error"
              className="p-2 text-sm rounded bg-red-500/10 text-red-300"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-white/5 font-bold text-sm hover:bg-slate-200 dark:hover:bg-white/10 transition-all"
              data-testid="staff-cancel"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-8 py-2.5 rounded-lg bg-primary text-white font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100"
              data-testid="staff-submit"
            >
              {saving ? 'Menyimpan...' : 'Simpan Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateStaffModal
